import { useState } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { hojeEmTexto, nomeDoMes, textoParaData, dataParaTexto } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, type LancamentoDaLista } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { LinhasDeLancamento } from "../lancamentos/LinhasDeLancamento";
import { LancamentoDialog } from "../lancamentos/LancamentoDialog";

const somar = (ls: LancamentoDaLista[]) => ls.reduce((t, l) => t + l.valorCents, 0);

function emDias(hoje: string, dias: number) {
  const d = textoParaData(hoje);
  d.setUTCDate(d.getUTCDate() + dias);
  return dataParaTexto(d);
}

function Grupo({ titulo, cor, linhas, podeLancar, onEditar }: { titulo: string; cor: string; linhas: LancamentoDaLista[]; podeLancar: boolean; onEditar: (l: LancamentoDaLista) => void }) {
  if (!linhas.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-foreground">
        <span className={cn("h-2 w-2 rounded-full", cor)} />
        {titulo}
        <span className="font-normal text-zinc-500 dark:text-muted-foreground">
          {linhas.length} · {reais(somar(linhas))}
        </span>
      </h3>
      <LinhasDeLancamento linhas={linhas} podeLancar={podeLancar} onEditar={onEditar} />
    </section>
  );
}

/** Contas em aberto por urgência (as vencidas de meses anteriores continuam aqui) e as pagas no mês. */
export function ContasAPagar({ competencia }: { competencia: string }) {
  const podeLancar = usePermissao("finance.manage");
  const dados = trpc.financeiro.aPagar.listar.useQuery({ competencia }).data;
  const [dialogo, setDialogo] = useState<{ editando: LancamentoDaLista | null } | null>(null);
  const [verPagas, setVerPagas] = useState(false);
  const hoje = hojeEmTexto();
  const semana = emDias(hoje, 7);
  const abertos = dados?.abertos ?? [];
  const vencidas = abertos.filter((l) => l.vencimento && l.vencimento < hoje);
  const proximas = abertos.filter((l) => l.vencimento && l.vencimento >= hoje && l.vencimento <= semana);
  const depois = abertos.filter((l) => !l.vencimento || l.vencimento > semana);
  const pagas = dados?.pagos ?? [];
  const editar = (l: LancamentoDaLista) => setDialogo({ editando: l });

  const resumo = [
    { rotulo: "Em aberto", valor: somar(abertos), sub: `${abertos.length} ${abertos.length === 1 ? "conta" : "contas"}`, cor: "text-zinc-900 dark:text-foreground" },
    { rotulo: "Vencidas", valor: somar(vencidas), sub: vencidas.length ? "Pague ou renegocie" : "Nenhuma", cor: vencidas.length ? "text-red-600" : "text-zinc-900 dark:text-foreground" },
    { rotulo: "Pagas no mês", valor: somar(pagas), sub: `${pagas.length} ${pagas.length === 1 ? "pagamento" : "pagamentos"}`, cor: "text-emerald-600" },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {resumo.map((c) => (
          <div key={c.rotulo} className="min-w-0 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm sm:p-4 dark:border-border dark:bg-card">
            <p className="truncate text-[11px] font-medium text-zinc-500 sm:text-xs dark:text-muted-foreground">{c.rotulo}</p>
            <p className={cn("mt-1 truncate text-sm font-bold tabular-nums sm:text-xl", c.cor)}>{reais(c.valor)}</p>
            <p className="hidden text-[11px] text-zinc-500 sm:block dark:text-muted-foreground">{c.sub}</p>
          </div>
        ))}
      </div>

      {podeLancar && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setDialogo({ editando: null })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Nova conta a pagar
          </button>
        </div>
      )}

      {dados && !abertos.length && (
        <div className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
          Nenhuma conta em aberto até o fim de {nomeDoMes(competencia)}.
        </div>
      )}
      <Grupo titulo="Vencidas" cor="bg-red-500" linhas={vencidas} podeLancar={podeLancar} onEditar={editar} />
      <Grupo titulo="Vencem nos próximos 7 dias" cor="bg-amber-500" linhas={proximas} podeLancar={podeLancar} onEditar={editar} />
      <Grupo titulo="Mais adiante" cor="bg-zinc-400" linhas={depois} podeLancar={podeLancar} onEditar={editar} />

      {pagas.length > 0 && (
        <section className="space-y-2">
          <button type="button" onClick={() => setVerPagas((v) => !v)} className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-700 dark:text-foreground">
            <ChevronDown className={cn("h-4 w-4 transition-transform", verPagas && "rotate-180")} />
            Pagas em {nomeDoMes(competencia)} ({pagas.length})
          </button>
          {verPagas && <LinhasDeLancamento linhas={pagas} podeLancar={podeLancar} onEditar={editar} />}
        </section>
      )}

      <LancamentoDialog aberto={!!dialogo} editando={dialogo?.editando} competencia={competencia} tipoInicial="SAIDA" onFechar={() => setDialogo(null)} />
    </div>
  );
}
